<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useSession } from './stores/session';
import { useContext } from './context';
import ProblemAlert from './components/ProblemAlert.vue';
import Icon from './components/Icon.vue';

const session = useSession();
const { auth } = useContext();
const error = ref<unknown>();
onMounted(() => session.load().catch((e) => (error.value = e)));

/** peak.<cluster>.… names the cluster the UI runs on (peak.stage.peaksolution.com → stage). */
const cluster = computed(() => /^peak\.([a-z0-9-]+)\./.exec(window.location.hostname)?.[1]);
const initials = computed(() => {
  const n = session.displayName.replace(/@.*$/, '');
  const parts = n.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? parts[0]?.[1] ?? '')).toUpperCase() || '?';
});
</script>

<template>
  <a class="skip" href="#main">Skip to content</a>
  <header class="top">
    <RouterLink to="/" class="brand">
      <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true"><rect width="26" height="26" rx="7" fill="#161A22" /><path d="M5 19 L11 9 L14 14 L16 11 L21 19 Z" fill="#FFFFFF" /></svg>
      Peak
    </RouterLink>
    <span v-if="cluster" class="pill tone-warn">{{ cluster }} cluster</span>
    <div class="spacer"></div>
    <div class="user">
      <template v-if="session.me">
        <span class="avatar" aria-hidden="true">{{ initials }}</span>
        <span data-testid="user">{{ session.displayName }}</span>
        <span v-if="session.me.admin" class="pill sm tone-outline">admin</span>
      </template>
      <button type="button" class="sm" @click="auth.logout()">Log out</button>
    </div>
  </header>
  <div class="shell">
    <nav class="side" aria-label="Main">
      <RouterLink to="/" class="nav-envs" exact-active-class="router-link-exact-active" :class="{ 'router-link-exact-active': $route.name === 'detail' }"><Icon name="envs" :size="18" /> Environments</RouterLink>
      <RouterLink to="/new"><Icon name="plus" :size="18" /> New environment</RouterLink>
    </nav>
    <main id="main" tabindex="-1">
      <div class="page" :class="{ narrow: $route.name === 'create' }">
        <ProblemAlert :error="error" context="Your entitlements could not be loaded" />
        <RouterView />
      </div>
    </main>
  </div>
</template>
